import { EventEmitter, Injectable, Output } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subject, ReplaySubject, Subscription } from 'rxjs';
import { Status, User } from './user';
import { AuthResponse } from './auth-response';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { BulkUploadService } from '../bulkupload.service';
import SessionExpiry from './sessionExpiry';
import { UserData } from './userData';
import { Utility } from '../utility/utility';
import { PermissionEnforcer } from '../services/permission-enforcer.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    AUTH_SERVER_ADDRESS: string = environment.apiUrl;
    authSubject = new BehaviorSubject(false);

    // isInitialized = new ReplaySubject(1);

    token: string;
    public content = new Subject<string>();
    isBool: Subject<Object> = new ReplaySubject<Object>();
    editProject: Subject<Object> = new ReplaySubject<Object>();

    public share = this.content.asObservable();
    public isName = new Subject<string>();
    public name = '';
    statuses: Status[] = [
        {
            id: 0,
            name: 'Enabled',
        },
        {
            id: 1,
            name: 'Disabled',
        },
    ];

    @Output() getLoggedInName: EventEmitter<any> = new EventEmitter();
    @Output() getLoggedInRole: EventEmitter<any> = new EventEmitter();
    @Output() getFormClearance: EventEmitter<any> = new EventEmitter();
    @Output() getRegistrationStatus: EventEmitter<any> = new EventEmitter();

    constructor(
        private httpClient: HttpClient,
        private router: Router,
        public toastController: ToastController,
        public BulkUploadService: BulkUploadService,
        private userData: UserData,
        private sessionExpiry: SessionExpiry,
        private utility: Utility,
        private permissionEnforcer: PermissionEnforcer,
    ) {
        this.init();
        // this.isInitialized.next(this.isAuthenticated);
    }

    async init() {
        this.content.next(await this.userData.getName());
        this.content.next(await this.userData.getName());

        this.sessionExpiry.startCheck();
    }

    public authStatusListner: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private isAuthenticated = new BehaviorSubject(null);

    async getIsAuth(): Promise<boolean> {
        let subscription: Subscription;
        const isAuthenticated_returnVal: boolean = await new Promise((resolve) => {
            subscription = this.isAuthenticated.subscribe((isAuthenticated_val) => {
                if (isAuthenticated_val !== null) {
                    resolve(isAuthenticated_val);
                }
            });
        });
        subscription.unsubscribe();
        return isAuthenticated_returnVal;
    }

    getAuthStatusListener() {
        return this.authStatusListner.asObservable();
    }

    register(user: User) {
        var queryparam = 'register?api=core';
        return this.httpClient.post<AuthResponse>(`${this.AUTH_SERVER_ADDRESS}${queryparam}`, user);
    }

    edit(user, changePassword = false) {
        if (!changePassword) {
            user.metadata.status = +user.status;
        }
        delete user.status;
        // console.log('user', user);
        return this.httpClient.post(`${this.AUTH_SERVER_ADDRESS}update?api=core`, user);
    }
    getUser(userId: string) {
        let ID = { id: userId };
        return this.httpClient.post(`${this.AUTH_SERVER_ADDRESS}?api=core`, ID);
    }

    async presentToast(onmessage: string) {
        const toast = await this.toastController.create({
            message: onmessage,
            duration: 2000,
            color: 'text-danger',
        });
        toast.present();
    }

    async presentToastWithOptions(onmessage: string, oncolor: string) {
        (await this.toastController.getTop())?.remove();
        const toast = await this.toastController.create({
            message: onmessage,
            position: 'top',
            color: oncolor,
            cssClass: 'toast-bg',
            duration: 7000,
            buttons: [
                {
                    side: 'end',
                    handler: () => {},
                },
                {
                    side: 'end',
                    text: 'Close',
                    role: 'cancel',
                    handler: () => {},
                },
            ],
        });
        await toast.present();

        const dismissToastOnClick = () => {
            if (toast.present) {
                toast.dismiss();
            }
            document.body.removeEventListener('click', dismissToastOnClick);
        };

        document.body.addEventListener('click', dismissToastOnClick);

        const { role } = await toast.onDidDismiss();
        this.getRegistrationStatus.emit(true);
    }

    login(user: User) {
        const loginButton: HTMLButtonElement = document.querySelector('#login-button');
        loginButton.disabled = true;

        this.httpClient.post(`${this.AUTH_SERVER_ADDRESS}login?api=core`, user).subscribe(
            async (res: AuthResponse) => {
                const user = await this.userData.setUser(res.user);
                if (!user) {
                    return;
                }
                this.presentToastWithOptions('welcome ' + user.fullname, 'success');
                this.getLoggedInName.emit(this.name);

                this.isBool.next(false);
                this.editProject.next(false);
                this.isAuthenticated.next(true);
                this.content.next(user.fullname);
                this.authStatusListner.next(true);

                const isInMobileView = this.utility.isInMobileView(); //window.innerWidth <= 700;
                // Load permissions after user is logged in and identified
                await this.permissionEnforcer.loadPermissions().toPromise();
                // console.log('It triggered');
                if (user.role === 'tutor') {
                    var manifest_link_tag = document.createElement('link');
                    manifest_link_tag.rel = 'manifest';
                    manifest_link_tag.href = 'manifest.webmanifest';
                    document.head.appendChild(manifest_link_tag);
                }

                // if (user.role === 'admin' || user.role === 'projectadmin') {
                //     this.router.navigate(['register']);
                // } else if (user.role === 'tutor' && isInMobileView) {
                //     this.router.navigate(['attendance-tutor']);
                // } else {
                //     this.router.navigate(['student-course-lesson']);
                // }
                if (this.permissionEnforcer.hasPermission('AdminPanel', 'view')) {
                    this.router.navigate(['register']);
                    return false;
                } else if (
                    this.permissionEnforcer.hasPermission('Attendance-Tutor', 'view') &&
                    isInMobileView
                ) {
                    this.router.navigate(['attendance-tutor']);
                    return false;
                } else if (
                    this.permissionEnforcer.hasPermission('Student-Course-Lessons', 'view')
                ) {
                    this.router.navigate(['student-course-lesson']);
                    return false;
                } else {
                    this.router.navigate(['/']);

                    loginButton.disabled = false;
                    return false;
                }
            },
            (error) => {
                this.authStatusListner.next(false);
                this.getLoggedInName.emit('Register');
                loginButton.disabled = false;
            },
        );
    }

    getUserRoles() {
        return this.httpClient.get(`${this.AUTH_SERVER_ADDRESS}user-roles?api=core`);
    }

    getSchoolGrade() {
        return this.httpClient.get(`${this.AUTH_SERVER_ADDRESS}school-grade-master?api=core`);
    }
    getTermScoreByTermID() {
        return this.httpClient.get(`${environment.apiBaseUrl}/api/v3/leaderboard?`);
    }

    async autoAuthUser() {
        const user = await this.userData.getUser();
        if (!user) {
            return;
        }
        await this.permissionEnforcer.loadPermissions().toPromise();
        this.isAuthenticated.next(true);
        this.authStatusListner.next(true);
        this.isBool.next(false);
        this.editProject.next(false);
        this.content.next(this.name);
    }

    async logout() {
        this.getFormClearance.emit(true);
        this.getLoggedInName.emit('Register');
        this.isAuthenticated.next(false);
        this.authStatusListner.next(false);
        this.httpClient.get(`${this.AUTH_SERVER_ADDRESS}logout?api=core`);
        await this.permissionEnforcer.loadPermissions()?.toPromise();
        await this.sessionExpiry.logout('abrupt close');
        // this.router.navigate(['/']);
        // await this.userData.remove();
    }

    saveBulk(bulkUploads: File) {
        const form = new FormData();
        form.append('bulkUpload', bulkUploads);
        return this.httpClient.post(`${environment.apiUrl}/bulkUserRegistration`, form);
    }

    updateBulkUsers(bulkUploads: File) {
        const form = new FormData();
        form.append('bulkUpload', bulkUploads);
        return this.httpClient.post(`${environment.apiUrl}/bulkUserUpdate`, form);
    }
}
